import { useNavigate } from "@tanstack/react-router";
import { useLoginMutation, useLogoutMutation } from "api/Auth/hooks.ts";
import queryClient from "api/queryClient.ts";
import { useGetMeQuery } from "api/User/hooks.ts";
import { sessionAtom } from "atoms/sessionAtom.ts";
import { tenantAtom } from "atoms/tenantAtom.ts";
import { useTableLocalStorage } from "components/UI/Table/hooks/useTableLocalStorage.ts";
import { useSession } from "hooks/useSession.ts";
import { useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { PermissionEnum, RoleEnum } from "types/enums";

import LoginRequest = App.Http.Requests.Auth.LoginRequest;
import UserData = App.Data.UserData;
import TenantData = App.Data.TenantData;

export const useUser = () => {
    const initialTenantState = useRecoilState(tenantAtom);
    const sessionUtils = useSession();
    const [tenantState, setTenantState] = initialTenantState;

    const navigate = useNavigate();
    const sessionState = useRecoilValue(sessionAtom);
    const pendingLoginTenantSlug = useRef<string | undefined>(undefined);

    const tableLocalStorageUtils = useTableLocalStorage();

    const getUserQuery = useGetMeQuery(
        {
            filter: {
                //include: "tenants,tenants.roles,tenants.permissions,tenants.categories",
                include: "tenants,tenants.roles,tenants.permissions",
            },
        },
        {
            enabled: false,
        }
    );

    const authenticateMutation = useLoginMutation({
        onSuccess: (response) => {
            queryClient.removeQueries({
                queryKey: ["me"],
            });
            loginUser(response?.token || "", response.user);
        },
    });

    const pendingLogoutRedirectSlug = useRef<string | undefined>(undefined);

    const logoutMutation = useLogoutMutation({
        onSuccess: async () => {
            // Prefer the slug the caller explicitly asked to redirect to (e.g. the no-access page,
            // where the user never had this tenant as their "active" one) over getActiveTenant().
            const tenantSlug = pendingLogoutRedirectSlug.current ?? getActiveTenant()?.slug_name;
            pendingLogoutRedirectSlug.current = undefined;
            await queryClient.clear();
            sessionUtils.removeSession();
            // Hard reload to clear all React state
            window.location.href = tenantSlug ? `/${tenantSlug}/auth/login` : "/auth/login";
        },
    });

    /*
     * Authenticate user with email and password then login into app
     */
    const authenticateUser = (params: LoginRequest, tenantSlug?: string) => {
        pendingLoginTenantSlug.current = tenantSlug;
        authenticateMutation.mutate(params);
    };

    /*
     * Login into app without authentication
     */
    const loginUser = (token: string, user: UserData, tenantSlug?: string) => {
        queryClient.removeQueries({ queryKey: ["me"] });
        sessionUtils.setToken(token);
        sessionUtils.setUser(user);
        const slug = tenantSlug ?? pendingLoginTenantSlug.current;
        pendingLoginTenantSlug.current = undefined;
        if (slug) {
            navigate({ to: "/$tenantSlug", params: { tenantSlug: slug } });
        } else {
            navigate({ to: "/account" });
        }
    };

    const logoutUser = (redirectTenantSlug?: string) => {
        tableLocalStorageUtils.clearAllTableConfigs(); // TODO -  It is hot fix - need to find clear solution.
        pendingLogoutRedirectSlug.current = redirectTenantSlug;
        logoutMutation.mutate();
    };

    const fetchUser = async () => {
        try {
            const response = await getUserQuery.refetch({
                throwOnError: true,
            });
            if (response && response.data && response.data.user) {
                sessionUtils.setUser(response.data.user);
            } else {
                logoutUser();
            }
        } catch (error) {
            console.log("refetch error", error);
        }
    };

    const matchTenant = async (tenantName: string | null) => {
        // get tenant slug from url params
        let slugName = tenantName;
        if (!slugName) {
            slugName = sessionState.user?.tenants?.[0].slug_name || "";
            navigate({ to: "/$tenantSlug", params: { tenantSlug: slugName } });
        }

        // try to find tenant by slug name in user tenants
        const tenant = sessionState.user?.tenants?.find((tenant) => tenant.slug_name === slugName) ?? null;

        if (!tenant) {
            navigate({ to: "/" });
            return null;
        }

        // set active tenant slug into atom state
        setTenant(tenant);

        // find and return active tenant from user tenants
        return tenant;
    };

    const setTenant = (tenant: TenantData | null) => {
        if (!tenant) {
            setTenantState(() => ({
                slug_name: null,
                currency: null,
            }));
        } else {
            setTenantState(() => ({ ...tenant, currency: null }));
        }
    };

    const getActiveTenant = (): TenantData | null => {
        const tenant = sessionState.user?.tenants?.find((tenant) => tenant.slug_name === tenantState.slug_name) ?? null;
        if (tenant) {
            return tenant;
        }
        return null;
    };

    const isAdmin = (): boolean => {
        return (
            getActiveTenant()?.userRoles?.some((role) =>
                [RoleEnum.AdminFree, RoleEnum.AdminBeast].includes(role.name)
            ) || false
        );
    };

    const isAffiliate = (): boolean => {
        return (
            getActiveTenant()?.userRoles?.some((role) =>
                [RoleEnum.AffiliatePartner, RoleEnum.AffiliateManager].includes(role.name)
            ) || false
        );
    };

    const hasPermissionInAnyTenant = (permission: PermissionEnum): boolean => {
        return (
            sessionState.user?.tenants?.some((tenant) =>
                tenant.userPermissions?.some((tenantPermission) => tenantPermission.name === permission)
            ) || false
        );
    };

    return {
        authenticateUser,
        loginUser,
        logoutUser,
        fetchUser,
        getAuthenticatedUser: () => sessionState.user,
        getActiveTenant,
        matchTenant,
        isAdmin,
        isAffiliate,
        hasPermissionInAnyTenant,
        setTenant,
    };
};
